import Api from '../Api';
import {
  fetchOrganizationAutofillCitiesData,
  stripJsonFromPastedText,
  normalizeContactDigits,
  findCityById,
} from './organizationAutofill';

const CONTACT_KEYS = ['phone_number', 'instagram', 'telegram', 'whatsapp', 'viber', 'facebook', 'website'];
const PHONE_DIGIT_CONTACT_KEYS = ['phone_number', 'whatsapp', 'viber'];

/**
 * Общий файл (без городов): только категории событий.
 */
export async function fetchEventAutofillCommonData() {
  const { data } = await Api.getEventCategories();
  return { eventCategories: data?.categories || [] };
}

/**
 * Данные для «Преобразовать»: город выбранной страны + категории событий.
 */
export async function fetchEventAutofillDataForSelectedCountry(countryId) {
  const [citiesPart, { eventCategories }] = await Promise.all([
    fetchOrganizationAutofillCitiesData(countryId),
    fetchEventAutofillCommonData(),
  ]);
  const { selectedCountry, cities } = citiesPart;
  return {
    eventCategories,
    selectedCountry,
    citiesByCountryId: { [selectedCountry._id]: cities },
  };
}

function findEventCategoryById(eventCategories, id) {
  if (!id) return null;
  return eventCategories.find((c) => c._id === id) || null;
}

/**
 * Частичное заполнение формы «Добавить мероприятие».
 */
export function parseEventAutofillJson(jsonString, staticData) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  if (!data || typeof data !== 'object') {
    return { error: 'Корень JSON должен быть объектом.' };
  }

  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  /** Ключ есть и значение не null — null значит «поле не трогать, не валидировать» */
  const take = (k) => has(k) && data[k] !== null;
  const { citiesByCountryId, eventCategories } = staticData;

  const patches = {};

  if (take('owner_id')) patches.ownerId = String(data.owner_id).trim();
  if (take('name')) patches.name = String(data.name).trim();
  if (take('specialization')) patches.specialization = String(data.specialization).trim();
  if (take('holding_date')) patches.holdingDate = String(data.holding_date).trim();
  if (take('date_start')) patches.dateStart = String(data.date_start).trim();
  if (take('date_end')) patches.dateEnd = String(data.date_end).trim();
  if (take('address')) patches.address = String(data.address).trim();
  if (take('description')) patches.description = String(data.description).trim();

  if (take('contacts') && data.contacts && typeof data.contacts === 'object') {
    patches.contactsPartial = {};
    CONTACT_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(data.contacts, k) && data.contacts[k] !== null) {
        const v = data.contacts[k];
        const raw = v != null ? String(v).trim() : '';
        patches.contactsPartial[k] = PHONE_DIGIT_CONTACT_KEYS.includes(k)
          ? normalizeContactDigits(raw)
          : raw;
      }
    });
  }

  if (take('events_category_id')) {
    const cat = findEventCategoryById(eventCategories, data.events_category_id);
    if (!cat) {
      return { error: 'Категория мероприятия не найдена по events_category_id.' };
    }
    patches.eventCategory = cat;
  }

  if (take('city_id')) {
    const city = findCityById(citiesByCountryId, data.city_id);
    if (!city) {
      return { error: 'Город не найден по city_id.' };
    }
    patches.city = city;
  }

  if (take('coordinates') && data.coordinates && (data.coordinates.lat != null || data.coordinates.lon != null)) {
    patches.coordinates = {
      lat: data.coordinates.lat != null ? String(data.coordinates.lat) : '',
      lon: data.coordinates.lon != null ? String(data.coordinates.lon) : '',
      is_special_point_on_map: Boolean(data.coordinates.is_special_point_on_map),
    };
  }

  return { patches };
}

function buildEventAutofillSchemaDescription() {
  return {
    description: 'Один JSON-объект для формы «Добавить мероприятие». В одном сообщении два фрагмента: общий файл (REFERENCE_COMMON: event_categories) и файл городов страны (REFERENCE_CITIES). city_id только из REFERENCE_CITIES.cities; events_category_id из REFERENCE_COMMON.event_categories.',
    fields: {
      owner_id: '24-символьный hex ID владельца',
      name: 'Название мероприятия',
      events_category_id: 'строка — _id из REFERENCE_COMMON.event_categories',
      specialization: 'Обязательно, если выбрана категория с name «Другое»; иначе по желанию',
      holding_date: 'Строка для отображения',
      date_start: 'YYYY-MM-DD или пусто',
      date_end: 'YYYY-MM-DD или пусто',
      city_id: 'строка — cities[i]._id из REFERENCE_CITIES.cities (страна в REFERENCE_CITIES.selected_country)',
      address: 'строка',
      coordinates: { lat: 'число или строка', lon: 'число или строка', is_special_point_on_map: false },
      contacts: 'phone_number, whatsapp, viber — только цифры подряд; остальные поля — строки',
      description: 'строка',
    },
    json_syntax: 'Ответ только markdown-блок ```json … ```, внутри один объект после JSON.stringify. Экранирование \\" для кавычек внутри строк.',
    note: 'Телефоны в contacts.phone_number, whatsapp, viber — только цифры. Категория «Другое» — обязательно specialization.',
  };
}

function eventAutofillPromptModelLines() {
  const schemaDescription = buildEventAutofillSchemaDescription();
  return [
    'Ты — помощник по заполнению карточки мероприятия в админке Nomad.',
    '',
    'Порядок работы:',
    '— Сначала в ОДНОМ сообщении приходят два фрагмента подряд: (А) общий файл без городов — event_categories как REFERENCE_COMMON; (Б) файл REFERENCE_CITIES с selected_country и cities. На это сообщение ты НЕ генерируешь JSON.',
    '— Если задача понятна, ответь: «Задача ясна, отправь текст для преобразования».',
    '— Когда пришлют текст о мероприятии, сформируй ответ по схеме.',
    '',
    'REFERENCE_COMMON.event_categories — глобальные категории афиш. REFERENCE_CITIES — город и страна только для city_id.',
    '',
    'КРИТИЧНО — категория (events_category_id):',
    '— Только _id из REFERENCE_COMMON.event_categories.',
    '— Если «Другое» — заполни specialization.',
    '',
    'КРИТИЧНО — телефоны (contacts.phone_number, contacts.whatsapp, contacts.viber):',
    '— Только цифры; +7 … → только цифры.',
    '— instagram, telegram, facebook, website не режь до цифр.',
    '',
    'КРИТИЧНО — даты date_start и date_end:',
    '— Формат YYYY-MM-DD или пустая строка.',
    '',
    'КРИТИЧНО — внутренние двойные кавычки в тексте:',
    '— Внутри строковых значений экранируй " как \\".',
    '',
    'КРИТИЧНО — город city_id:',
    '— Только _id из REFERENCE_CITIES.cities.',
    '',
    'КРИТИЧНО — формат ответа:',
    '— Всегда возвращай результат ТОЛЬКО внутри блока кода с типом json:',
    '```json',
    '{...}',
    '```',
    '— Без текста до ```json и после ```.',
    '— Один объект для JSON.parse; кавычки в строках только \\".',
    '',
    'Когда пришёл текст для преобразования:',
    '1) Заполни поля из schemaDescription.fields.',
    '2) events_category_id — REFERENCE_COMMON; city_id — REFERENCE_CITIES.',
    '3) Ответ одним блоком ```json … ```.',
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
    '',
    'REFERENCE_COMMON:',
  ];
}

/** Файл 1: категории мероприятий без городов. */
export function buildEventAutofillCommonFileContent({ eventCategories }) {
  const ec = eventCategories
    .slice()
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((c) => ({ _id: c._id, name: c.name, sort: c.sort }));
  const referenceCommon = { event_categories: ec };
  const prompt = [...eventAutofillPromptModelLines(), JSON.stringify(referenceCommon, null, 2)].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — автозаполнение мероприятия: ОБЩИЙ файл (без городов)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Общий файл один для всех стран (только категории событий). Второй — города страны — скачивать отдельно для каждой страны.',
    '2) Первым сообщением вставьте в чат модели общий файл целиком, сразу за ним (в том же сообщении) файл городов нужной страны.',
    '3) Дождитесь от модели: «Задача ясна, отправь текст для преобразования».',
    '4) Пришлите текст о мероприятии.',
    '5) Ответ модели с блоком ```json можно вставить в поле ниже; в админке выберите ту же страну, что во втором файле — «Преобразовать».',
    '6) Если JSON отклонён системой — https://jsonviewer.stack.hu/, «Format», исправление по подсказкам.',
    '',
    prompt,
  ].join('\n');
}

/** Файл 2: города страны для мероприятий. */
export function buildEventAutofillCitiesFileContent({ selectedCountry, cities }) {
  if (!selectedCountry?._id) {
    throw new Error('selectedCountry is required');
  }
  const countryId = selectedCountry._id;
  const referenceCities = {
    selected_country: { _id: selectedCountry._id, name: selectedCountry.name },
    cities: (cities || []).map((city) => ({
      _id: city._id,
      name: city.name,
      country_id: city.country_id || countryId,
    })),
    note_model: `Города для мероприятий (${selectedCountry.name}). Категории — в общем файле.`,
  };

  return [
    '══════════════════════════════════════════════════════════════',
    `NOMAD — автозаполнение мероприятия: ГОРОДА — ${selectedCountry.name}`,
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '— Приложение к общему файлу мероприятий. Для каждой страны — свой такой файл. Оба отправляются вместе одним сообщением.',
    '',
    'REFERENCE_CITIES:',
    JSON.stringify(referenceCities, null, 2),
  ].join('\n');
}
